import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { Box, Chip, List, ListItem, ListItemButton, Paper, Popover } from "@mui/material";
import { toast } from "react-toastify";
import { COLORS, FONT_WEIGHTS } from "../../common/consts";
import Text, { TextVariant } from "../Display/Text";
import Button from "../Display/Button";
import Tabs from "../Display/Tabs";
import Input from "../DataInput/Input";
import Select from "../DataInput/Select";
import {
  ExternalIcon,
  OU,
  OUTREACH_SANS,
  OuControl,
  OuEmptyState,
  OuIconButton,
  OuNotice,
  OuPage,
  OuResultCount,
  OuSearch,
  OuSecondaryButton,
  OuSelect,
  OuTable,
  OuTableColumn,
  OuTooltip,
  RefreshIcon,
} from "../CrmUi";
import { getExportHistory } from "../../services/firebase";
import { ExportEntityType, ExportHistoryResult, ExportHistoryRow } from "../../types/exportHistory";

/**
 * Settings → History: every LinkedIn export made through the extension, one
 * row per export, so re-exporting someone adds a row instead of replacing one.
 * The columns, the People / Companies split, the search fields and the CSV of
 * the selected rows are the ones the old Export extension's EXPORT HISTORY
 * table had; the table itself is drawn like Outreach's Prospects list. Admins
 * see the organization or pick one rep (Exported by, asked of the server),
 * everyone else their own exports.
 */

type DateMode = "on" | "between";
const ALL = "all";
const ME = "me";

type FilterType = "date" | "destination" | "action" | "emailStatus" | "searchIn";
type Operator = "is" | "is not";
type SortKey = "newest" | "oldest" | "name";

interface ValuesFilter {
  operator: Operator;
  values: string[];
}

interface Filters {
  /** "YYYY-MM-DD" bounds, sent to the server; the same day twice for a single day. */
  date?: { from: string; to: string } | null;
  destination?: ValuesFilter | null;
  action?: ValuesFilter | null;
  emailStatus?: ValuesFilter | null;
  /** Which fields the search box looks in; all of them when unset. */
  searchIn?: ValuesFilter | null;
}

const FILTER_LABELS: Record<FilterType, string> = {
  date: "Exported",
  destination: "Destination",
  action: "Result",
  emailStatus: "Email status",
  searchIn: "Search in",
};

const DESTINATION_LABELS: Record<string, string> = { outreach: "Outreach", hubspot: "HubSpot", salesforce: "Salesforce" };
const NO_EMAIL = "none";
const UNVERIFIED = "unverified";

/**
 * Outreach's own Email status wording. The export stores valid, invalid,
 * questionable or unknown; the other spellings are what verifiers say for the
 * same thing. Anything else (unknown included) is left blank, as Outreach does.
 */
const EMAIL_STATUS_LABELS: Record<string, string> = {
  valid: "Valid",
  invalid: "Invalid",
  questionable: "Questionable",
  catch_all: "Questionable",
  "catch-all": "Questionable",
  catchall: "Questionable",
  accept_all: "Questionable",
  risky: "Questionable",
};
const emailStatusLabel = (status: string | null) => (status ? EMAIL_STATUS_LABELS[status.toLowerCase()] || "" : "");
/** The value the Email status filter matches on: the label's key, unverified, or no email at all. */
const emailStatusKey = (r: ExportHistoryRow) => (!r.email ? NO_EMAIL : emailStatusLabel(r.emailStatus).toLowerCase() || UNVERIFIED);

/* The old table's search filters: Email, Name, Company, Title. */
const SEARCH_FIELDS: { value: string; label: string; get: (r: ExportHistoryRow) => (string | null)[] }[] = [
  { value: "email", label: "Email", get: (r) => [r.email] },
  { value: "name", label: "Name", get: (r) => [r.name, r.firstName, r.lastName, vanityOf(r.linkedinUrl)] },
  { value: "company", label: "Company", get: (r) => [r.companyName] },
  { value: "title", label: "Title", get: (r) => [r.title, r.headline] },
];

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const destinationLabel = (d: string | null) => (d ? DESTINATION_LABELS[d] || capitalize(d) : "");
const exporterLabel = (r: ExportHistoryRow) => r.exportedByName || r.exportedByEmail || "";

/** "jane-doe" from linkedin.com/in/jane-doe, for rows that predate the history and have no name. */
function vanityOf(url: string): string | null {
  const m = String(url || "").match(/\/(?:in|company|school)\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

const displayName = (r: ExportHistoryRow) => r.name || vanityOf(r.linkedinUrl) || r.linkedinUrl;

/* Outreach writes dates out: "September 29, 2026". */
const formatDate = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
const formatDateTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

/** LinkedIn's "2 yrs 3 mos", counted from the role's start to the day of the export. */
function jobDuration(start: string | null, at: number): string | null {
  const m = String(start || "").match(/^(\d{4})(?:-(\d{1,2}))?/);
  if (!m) return null;
  const end = new Date(at);
  const months = (end.getFullYear() - Number(m[1])) * 12 + (end.getMonth() + 1 - Number(m[2] || 1)) + 1;
  if (months < 1) return null;
  const y = Math.floor(months / 12);
  const mo = months % 12;
  return [y ? `${y} yr${y > 1 ? "s" : ""}` : "", mo ? `${mo} mo${mo > 1 ? "s" : ""}` : ""].filter(Boolean).join(" ");
}

/** The email was checked during the export, so a verified status dates from it. */
const verifiedAt = (r: ExportHistoryRow) => (r.email && emailStatusLabel(r.emailStatus) ? r.exportedAt : null);

const dayStart = (d: string) => (d ? new Date(`${d}T00:00:00`).getTime() : null);
const dayEnd = (d: string) => (d ? new Date(`${d}T23:59:59.999`).getTime() : null);

const matchesValues = (filter: ValuesFilter | null | undefined, value: string) =>
  !filter || filter.values.length === 0 || (filter.operator === "is" ? filter.values.includes(value) : !filter.values.includes(value));

/* ── CSV, the old extension's columns plus where each export went ── */

const PEOPLE_CSV: [string, (r: ExportHistoryRow) => unknown][] = [
  ["Email", (r) => r.email],
  ["Email Status", (r) => emailStatusLabel(r.emailStatus)],
  ["Email Verification Date", (r) => (verifiedAt(r) ? new Date(r.exportedAt).toISOString() : "")],
  ["Name", (r) => r.name],
  ["First Name", (r) => r.firstName],
  ["Last Name", (r) => r.lastName],
  ["Title", (r) => r.title],
  ["Company", (r) => r.companyName],
  ["Location", (r) => r.location],
  ["City", (r) => r.city],
  ["State", (r) => r.state],
  ["Country", (r) => r.country],
  ["Timezone", (r) => r.timezone],
  ["Headline", (r) => r.headline],
  ["About", (r) => r.about],
  ["Current Duration", (r) => jobDuration(r.jobStartDate, r.exportedAt)],
  ["Phone", (r) => r.phone],
  ["LinkedIn URL", (r) => r.linkedinUrl],
  ["LinkedIn ID", (r) => r.linkedinId],
  ["LinkedIn URN", (r) => r.linkedinUrn],
  ["Sales Navigator URN", (r) => r.salesNavUrn],
  ["Profile Picture", (r) => r.pictureUrl],
  ["Exported By", (r) => r.exportedByEmail || r.exportedByName],
  ["Destination", (r) => destinationLabel(r.destination)],
  ["Result", (r) => r.action],
  ["Error", (r) => r.error],
  ["Record ID", (r) => r.recordId],
  ["Record URL", (r) => r.recordUrl],
  ["Exported At", (r) => new Date(r.exportedAt).toISOString()],
];

const COMPANY_CSV: [string, (r: ExportHistoryRow) => unknown][] = [
  ["Company Name", (r) => r.name],
  ["Industry", (r) => r.industry],
  ["Website", (r) => r.website],
  ["Employees", (r) => r.employees],
  ["Location", (r) => r.location],
  ["Description", (r) => r.description],
  ["LinkedIn URL", (r) => r.linkedinUrl],
  ["LinkedIn ID", (r) => r.linkedinId],
  ["LinkedIn URN", (r) => r.linkedinUrn],
  ["Sales Navigator URN", (r) => r.salesNavUrn],
  ["Exported By", (r) => r.exportedByEmail || r.exportedByName],
  ["Destination", (r) => destinationLabel(r.destination)],
  ["Result", (r) => r.action],
  ["Error", (r) => r.error],
  ["Record ID", (r) => r.recordId],
  ["Record URL", (r) => r.recordUrl],
  ["Exported At", (r) => new Date(r.exportedAt).toISOString()],
];

const escapeCSV = (field: unknown): string => {
  if (field === null || field === undefined) return "";
  const s = String(field);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function downloadCSV(rows: ExportHistoryRow[], entityType: ExportEntityType) {
  const spec = entityType === "company" ? COMPANY_CSV : PEOPLE_CSV;
  const content = [spec.map(([h]) => escapeCSV(h)).join(","), ...rows.map((r) => spec.map(([, get]) => escapeCSV(get(r))).join(","))].join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `exported_${entityType === "company" ? "companies" : "profiles"}_${new Date().toISOString().split("T")[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

/* ── cells: plain 14px values on one line, blank when empty ── */

const Cell: FC<{ value: ReactNode; title?: string | null }> = ({ value, title }) => (
  <div className="ou-cell" title={title || (typeof value === "string" ? value : undefined)}>
    {value}
  </div>
);

const NameLink = styled("a")`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 600 14px/20px ${OUTREACH_SANS};
  /* Outreach's name reads as ink, not link blue, until hovered. */
  && {
    color: ${OU.ink};
    font-weight: 600;
  }
  &&:hover {
    color: ${OU.brand};
  }
`;

const Secondary = styled("div")`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

const AvatarBox = styled("div")<{ square?: boolean }>`
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: ${({ square }) => (square ? "6px" : "50%")};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${OU.soft};
  color: ${OU.brand};
  font: 600 12px/1 ${OUTREACH_SANS};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const initialsOf = (name: string) =>
  name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");

/** The LinkedIn photo (or company logo); initials when there is none or LinkedIn's link has expired. */
const Avatar: FC<{ row: ExportHistoryRow; name: string }> = ({ row, name }) => {
  const [failed, setFailed] = useState(false);
  const src = !failed ? row.pictureUrl : null;
  return (
    <AvatarBox square={row.entityType === "company"} aria-hidden="true">
      {src ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} /> : initialsOf(name)}
    </AvatarBox>
  );
};

const NameStack = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  > div:last-child {
    min-width: 0;
  }
`;

/** Name over "Title, Company" for people, over the industry for companies, like Outreach's Name column. */
const NameCell: FC<{ row: ExportHistoryRow }> = ({ row }) => {
  const name = displayName(row);
  const secondary =
    row.entityType === "company"
      ? row.industry || row.headline
      : [row.title, row.companyName].filter(Boolean).join(", ") || row.headline || (row.legacy ? "Exported before the history was kept" : "");
  return (
    <NameStack>
      <Avatar row={row} name={name} />
      <div>
        {row.linkedinUrl ? (
          <NameLink href={row.linkedinUrl} target="_blank" rel="noopener noreferrer" title={`${name} on LinkedIn`}>
            {name}
          </NameLink>
        ) : (
          <div className="ou-cell" style={{ fontWeight: 600 }}>
            {name}
          </div>
        )}
        {secondary && <Secondary title={secondary}>{secondary}</Secondary>}
      </div>
    </NameStack>
  );
};

/* The dashboard's own email status colors, as on Insights. */
const STATUS_DOT: Record<string, string> = {
  Valid: COLORS.chartEmailValid,
  Questionable: COLORS.chartEmailQuestionable,
  Invalid: COLORS.chartEmailInvalid,
};

/** Outreach's wording with a colored dot in front: green valid, amber questionable, red invalid. */
const EmailStatusCell: FC<{ row: ExportHistoryRow }> = ({ row }) => {
  const label = row.email ? emailStatusLabel(row.emailStatus) : "";
  if (!label) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <span aria-hidden="true" style={{ flex: "0 0 auto", width: 8, height: 8, borderRadius: "50%", background: STATUS_DOT[label] }} />
      <div className="ou-cell">{label}</div>
    </div>
  );
};

const CopyButton = styled("button")`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${OU.muted};
  cursor: pointer;

  &:hover {
    background: ${OU.soft};
    color: ${OU.brand};
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

/** Just the address, with a copy action that shows on the row under the pointer. */
const EmailCell: FC<{ row: ExportHistoryRow }> = ({ row }) => {
  if (!row.email) return null;
  const email = row.email;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
      <div className="ou-cell" title={email}>
        {email}
      </div>
      <OuTooltip title="Copy email">
        <CopyButton
          type="button"
          className="ou-row-action"
          aria-label={`Copy ${email}`}
          onClick={() =>
            navigator.clipboard
              .writeText(email)
              .then(() => toast.success("Email copied"))
              .catch(() => toast.error("Could not copy the email"))
          }
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1Zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H8V7h11v14Z" />
          </svg>
        </CopyButton>
      </OuTooltip>
    </div>
  );
};

/** Plain text like every other value; a failure reads in the danger ink and says why on hover. */
const ResultCell: FC<{ row: ExportHistoryRow }> = ({ row }) =>
  row.action ? (
    <div className="ou-cell" title={row.error || undefined} style={row.action === "failed" ? { color: "var(--ou-danger-fg, #b42318)" } : undefined}>
      {capitalize(row.action)}
    </div>
  ) : null;

const RecordCell: FC<{ row: ExportHistoryRow }> = ({ row }) => {
  if (!row.recordUrl) return <Cell value={row.recordId} />;
  return (
    <div className="ou-cell">
      <a href={row.recordUrl} target="_blank" rel="noopener noreferrer">
        Open in {destinationLabel(row.destination) || "CRM"}
        <ExternalIcon />
      </a>
    </div>
  );
};

const WebsiteCell: FC<{ site: string | null }> = ({ site }) => {
  if (!site) return null;
  const href = /^https?:\/\//i.test(site) ? site : `https://${site}`;
  return (
    <div className="ou-cell" title={site}>
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 400 }}>
        {site.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "")}
      </a>
    </div>
  );
};

/* ── columns ── */

type Column = OuTableColumn<ExportHistoryRow>;

const text = (key: keyof ExportHistoryRow, header: string, width = 160): Column => ({
  key,
  header,
  width,
  render: (r) => <Cell value={r[key] as string | null} />,
});

const sharedColumns: Column[] = [
  { key: "exportedAt", header: "Exported", width: 200, render: (r) => <Cell value={formatDateTime(r.exportedAt)} /> },
  { key: "exportedBy", header: "Exported by", width: 180, render: (r) => <Cell value={exporterLabel(r)} title={r.exportedByEmail} /> },
  { key: "destination", header: "Destination", width: 120, render: (r) => <Cell value={destinationLabel(r.destination)} /> },
  { key: "action", header: "Result", width: 110, render: (r) => <ResultCell row={r} /> },
  { key: "record", header: "Record", width: 170, render: (r) => <RecordCell row={r} /> },
];

/* People: the old table's Profile (now Name), Company, Email, Job Duration,
   Location, City, State, Country, Timezone, Headline, About, LinkedIn ID,
   LinkedIn URN, Sales Navigator URN and Revealed (now Exported), with Email
   status and Email verification date beside the email as Outreach has them. */
const peopleColumns: Column[] = [
  { key: "name", header: "Name", width: 300, sticky: true, render: (r) => <NameCell row={r} /> },
  { key: "email", header: "Email", width: 260, render: (r) => <EmailCell row={r} /> },
  { key: "emailStatus", header: "Email status", width: 130, render: (r) => <EmailStatusCell row={r} /> },
  {
    key: "emailVerifiedAt",
    header: "Email verification date",
    width: 160,
    render: (r) => {
      const at = verifiedAt(r);
      return <Cell value={at ? formatDate(at) : ""} />;
    },
  },
  text("companyName", "Company", 180),
  { key: "jobDuration", header: "Job duration", width: 130, render: (r) => <Cell value={jobDuration(r.jobStartDate, r.exportedAt)} /> },
  text("location", "Location", 200),
  text("city", "City", 130),
  text("state", "State", 130),
  text("country", "Country", 130),
  text("timezone", "Timezone", 150),
  text("phone", "Phone", 150),
  text("headline", "Headline", 240),
  text("about", "About", 240),
  text("linkedinId", "LinkedIn ID", 150),
  text("linkedinUrn", "LinkedIn URN", 220),
  text("salesNavUrn", "Sales Navigator URN", 220),
  ...sharedColumns,
];

/* Companies: the old table's Company, Industry, Website, Employees, Location,
   Description, LinkedIn ID, LinkedIn URN, Sales Navigator URN and Revealed. */
const companyColumns: Column[] = [
  { key: "name", header: "Company", width: 300, sticky: true, render: (r) => <NameCell row={r} /> },
  text("industry", "Industry", 180),
  { key: "website", header: "Website", width: 200, render: (r) => <WebsiteCell site={r.website} /> },
  text("employees", "Employees", 120),
  text("location", "Location", 200),
  text("description", "Description", 260),
  text("linkedinId", "LinkedIn ID", 150),
  text("linkedinUrn", "LinkedIn URN", 220),
  text("salesNavUrn", "Sales Navigator URN", 220),
  ...sharedColumns,
];

/* ── filters: the Users page's chips and popovers ── */

const popoverPaperSx = {
  marginTop: "8px",
  borderRadius: "8px",
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1) !important",
  backgroundColor: "#f9f9fb",
};

const selectSx = {
  width: "100%",
  "& .MuiOutlinedInput-root, & .MuiInputBase-root": { backgroundColor: "var(--color-backgroundWhite, #ffffff) !important", width: "100% !important" },
};

const FilterChip: FC<{ label: ReactNode; onClick: () => void; onDelete: () => void }> = ({ label, onClick, onDelete }) => (
  <Chip
    label={label}
    onClick={onClick}
    onDelete={(e) => {
      e.stopPropagation();
      onDelete();
    }}
    deleteIcon={
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" style={{ width: "14px", height: "14px" }}>
        <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
        <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
      </svg>
    }
    sx={{
      backgroundColor: "#ffffff",
      color: "#5e5eaf",
      border: "1px solid rgba(89, 81, 255, 0.24) !important",
      borderRadius: "20px",
      height: "36px",
      paddingLeft: "12px",
      paddingRight: "12px",
      fontFamily: OUTREACH_SANS,
      fontWeight: FONT_WEIGHTS.light,
      fontSize: "0.875rem",
      cursor: "pointer",
      "&:hover": { backgroundColor: "#f9f9fb" },
      "& .MuiChip-label": { color: "#5e5eaf", fontWeight: FONT_WEIGHTS.light, cursor: "pointer", paddingLeft: 0, paddingRight: 0 },
      "& .MuiChip-deleteIcon": {
        width: "14px !important",
        height: "14px !important",
        marginRight: 0,
        marginLeft: "8px",
        "& circle": { fill: "#5e5eaf !important", transition: "fill 0.2s ease" },
        "& path": { fill: "#ffffff !important" },
        "&:hover circle": { fill: "#4a4a9f !important" },
      },
    }}
  />
);

/* ── layout ── */

/* Search, filters and Add filter on the left; count, sort and actions on the right, as on Outreach's lists. */
const Toolbar = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
`;

const ToolbarSide = styled("div")`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const SortControl = styled(OuControl)`
  width: 170px;
`;

const ExporterControl = styled(OuControl)`
  width: 200px;
`;

const Caption = styled("p")`
  margin: 0;
  font: 400 14px/20px ${OUTREACH_SANS};
  color: ${OU.muted};
`;

const DownloadIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8Z" />
  </svg>
);

const HistoryIcon: FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18Zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12Z" />
  </svg>
);

/* ── the section ── */

const ExportHistorySection: FC = () => {
  const [data, setData] = useState<ExportHistoryResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<ExportEntityType>("person");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [filters, setFilters] = useState<Filters>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLElement | null>(null);
  const [configAnchorEl, setConfigAnchorEl] = useState<HTMLElement | null>(null);
  const [editingFilter, setEditingFilter] = useState<FilterType | null>(null);
  const [draftOperator, setDraftOperator] = useState<Operator>("is");
  const [draftValues, setDraftValues] = useState<string[]>([]);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [draftMode, setDraftMode] = useState<DateMode>("on");
  /** Whose exports, asked of the server: null until picked (an admin then sees everyone, a rep their own). */
  const [exporter, setExporter] = useState<string | null>(null);

  const dateFrom = filters.date?.from || "";
  const dateTo = filters.date?.to || "";

  // The date range and the exporter are what the server applies; the rest work on what came back.
  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setData(await getExportHistory({ from: dayStart(dateFrom), to: dayEnd(dateTo), ...(exporter ? { exportedBy: exporter } : {}) }));
    } catch (e: any) {
      setLoadError(e?.message || "Could not load the export history.");
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, exporter]);

  useEffect(() => {
    load();
  }, [load]);

  const allRows = useMemo(() => data?.rows || [], [data]);

  const options = useMemo(() => {
    const unique = (values: (string | null)[]) => Array.from(new Set(values.filter((v): v is string => !!v)));
    return {
      destination: unique(allRows.map((r) => r.destination)).map((d) => ({ value: d, label: destinationLabel(d) })),
      action: ["created", "updated", "skipped", "failed"].map((a) => ({ value: a, label: capitalize(a) })),
      emailStatus: [
        { value: "valid", label: "Valid" },
        { value: "questionable", label: "Questionable" },
        { value: "invalid", label: "Invalid" },
        { value: UNVERIFIED, label: "No status" },
        { value: NO_EMAIL, label: "No email" },
      ],
      searchIn: SEARCH_FIELDS.map((f) => ({ value: f.value, label: f.label })),
    };
  }, [allRows]);

  const rows = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    const fields = filters.searchIn?.values.length ? SEARCH_FIELDS.filter((f) => matchesValues(filters.searchIn, f.value)) : null;
    const matched = allRows.filter((r) => {
      if (r.entityType !== entityType) return false;
      if (!matchesValues(filters.destination, r.destination || "")) return false;
      if (!matchesValues(filters.action, r.action || "")) return false;
      if (entityType === "person" && !matchesValues(filters.emailStatus, emailStatusKey(r))) return false;
      if (!term) return true;
      const haystack = fields
        ? fields.flatMap((f) => f.get(r))
        : [
            ...SEARCH_FIELDS.flatMap((f) => f.get(r)),
            r.location,
            r.industry,
            r.website,
            r.linkedinUrl,
            r.linkedinId,
            r.phone,
            r.exportedByName,
            r.exportedByEmail,
            formatDateTime(r.exportedAt),
          ];
      return haystack.some((v) => !!v && v.toLowerCase().includes(term));
    });
    if (sortKey === "oldest") return [...matched].sort((a, b) => a.exportedAt - b.exportedAt);
    if (sortKey === "name") return [...matched].sort((a, b) => displayName(a).localeCompare(displayName(b)));
    return matched;
  }, [allRows, entityType, filters, searchQuery, sortKey]);

  const selectedRows = rows.filter((r) => selected.has(r.id));
  const counts = useMemo(
    () => ({ person: allRows.filter((r) => r.entityType === "person").length, company: allRows.filter((r) => r.entityType === "company").length }),
    [allRows]
  );

  const switchTab = (next: ExportEntityType) => {
    setEntityType(next);
    setSelected(new Set());
  };

  const openFilter = (type: FilterType, anchor: HTMLElement | null) => {
    setEditingFilter(type);
    if (type === "date") {
      const from = filters.date?.from || "";
      const to = filters.date?.to || "";
      setDraftMode(!filters.date || from === to ? "on" : "between");
      setDraftFrom(from || (filters.date ? "" : new Date().toLocaleDateString("en-CA")));
      setDraftTo(to);
    } else {
      const current = filters[type] as ValuesFilter | null | undefined;
      setDraftOperator(current?.operator || "is");
      setDraftValues(current?.values || []);
    }
    setConfigAnchorEl(anchor);
  };

  const closeConfig = () => {
    setEditingFilter(null);
    setConfigAnchorEl(null);
  };

  const applyFilter = () => {
    if (editingFilter === "date") {
      const range = draftMode === "on" ? { from: draftFrom, to: draftFrom } : { from: draftFrom, to: draftTo };
      setFilters({ ...filters, date: range.from || range.to ? range : null });
    } else if (editingFilter) {
      setFilters({ ...filters, [editingFilter]: draftValues.length ? { operator: draftOperator, values: draftValues } : null });
    }
    setSelected(new Set());
    closeConfig();
  };

  const chipLabel = (type: FilterType): ReactNode => {
    if (type === "date") {
      const { from, to } = filters.date || { from: "", to: "" };
      const range = from && from === to ? `on ${from}` : from && to ? `${from} to ${to}` : from ? `since ${from}` : `until ${to}`;
      return (
        <span>
          <span style={{ fontWeight: FONT_WEIGHTS.medium }}>{FILTER_LABELS.date}</span> {range}
        </span>
      );
    }
    const f = filters[type] as ValuesFilter;
    const list = options[type];
    const labelOf = (v: string) => String(list.find((o: { value: string }) => o.value === v)?.label || v);
    return (
      <span>
        <span style={{ fontWeight: FONT_WEIGHTS.medium }}>{FILTER_LABELS[type]}</span>{" "}
        {f.values.length === 1 ? `${f.operator} ${labelOf(f.values[0])}` : `${f.operator} ${f.values.length} values`}
      </span>
    );
  };

  const availableFilters: FilterType[] = [
    "date",
    "destination",
    "action",
    ...(entityType === "person" ? (["emailStatus"] as FilterType[]) : []),
    "searchIn",
  ];
  const activeFilters = availableFilters.filter((t) => !!filters[t]);
  const hasFilters = !!searchQuery.trim() || activeFilters.length > 0;
  const csvRows = selectedRows.length ? selectedRows : rows;

  const handleExportCSV = () => {
    if (csvRows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    downloadCSV(csvRows, entityType);
  };

  const showTable = !loadError && (isLoading || rows.length > 0);

  // The Exported by picker: the server's answer says whose rows these are.
  const exporterValue = !data ? exporter || ALL : !data.exportedBy ? ALL : data.exportedBy === data.me ? ME : data.exportedBy;
  const exporterOptions = useMemo(() => {
    if (!data?.isAdmin) return [];
    const others = (data.exporters || []).filter((m) => m.uid !== data.me).map((m) => ({ value: m.uid, label: m.name || m.email || "Unknown user" }));
    const picked = data.exportedBy && data.exportedBy !== data.me && !others.some((o) => o.value === data.exportedBy) ? [{ value: data.exportedBy, label: "Someone else" }] : [];
    return [{ value: ALL, label: "Everyone" }, { value: ME, label: "Mine" }, ...others, ...picked];
  }, [data]);
  const pickExporter = (value: string) => {
    setExporter(value);
    setSelected(new Set());
  };

  return (
    <OuPage>
      <Caption>
        {data && (!data.isAdmin || exporterValue === ME)
          ? "Every LinkedIn profile and company you exported with the boring extension, one row per export."
          : data && exporterValue !== ALL
            ? "Every LinkedIn profile and company this rep exported with the boring extension, one row per export."
            : "Every LinkedIn profile and company exported with the boring extension across your organization, one row per export."}
        {data?.allMyExportsUrl && (
          <>
            {" "}
            <a href={data.allMyExportsUrl} target="_blank" rel="noopener noreferrer">
              Your exports in HubSpot
              <ExternalIcon />
            </a>
          </>
        )}
      </Caption>

      <Tabs
        options={[
          { label: `People${data ? ` (${counts.person.toLocaleString()})` : ""}`, value: "person", onClick: () => switchTab("person") },
          { label: `Companies${data ? ` (${counts.company.toLocaleString()})` : ""}`, value: "company", onClick: () => switchTab("company") },
        ]}
      />

      <Toolbar>
        <ToolbarSide style={{ flex: "1 1 360px" }}>
          <OuSearch value={searchQuery} onChange={setSearchQuery} aria-label="Search exports" />
          {data?.isAdmin && (
            <ExporterControl>
              <OuSelect value={exporterValue} onChange={(v) => pickExporter(String(v))} aria-label="Exported by" options={exporterOptions} />
            </ExporterControl>
          )}
          {activeFilters.map((type) => (
            <FilterChip key={type} label={chipLabel(type)} onClick={() => openFilter(type, filterButtonEl)} onDelete={() => setFilters({ ...filters, [type]: null })} />
          ))}
          <OuSecondaryButton
            onClick={(e) => {
              setFilterButtonEl(e.currentTarget);
              setFilterAnchorEl(e.currentTarget);
            }}
          >
            Add filter
          </OuSecondaryButton>
        </ToolbarSide>
        <ToolbarSide>
          <OuResultCount aria-live="polite">{isLoading && !data ? "" : `${rows.length.toLocaleString()} results`}</OuResultCount>
          <SortControl>
            <OuSelect
              value={sortKey}
              onChange={(v) => setSortKey(v as SortKey)}
              aria-label="Sort by"
              options={[
                { value: "newest", label: "Newest first" },
                { value: "oldest", label: "Oldest first" },
                { value: "name", label: "Name" },
              ]}
            />
          </SortControl>
          <OuTooltip title={selectedRows.length ? `Export ${selectedRows.length} selected to CSV` : `Export all ${rows.length} to CSV`}>
            <span>
              <OuIconButton aria-label="Export CSV" onClick={handleExportCSV} disabled={csvRows.length === 0} label={selectedRows.length || undefined}>
                <DownloadIcon />
              </OuIconButton>
            </span>
          </OuTooltip>
          <OuTooltip title="Refresh">
            <span>
              <OuIconButton aria-label="Refresh" onClick={load} disabled={isLoading}>
                <RefreshIcon />
              </OuIconButton>
            </span>
          </OuTooltip>
        </ToolbarSide>
      </Toolbar>

      {data?.truncated && !isLoading && (
        <Caption>Showing the latest {data.rows.length.toLocaleString()} exports. Add an Exported filter to see older ones.</Caption>
      )}

      {loadError && !isLoading && (
        <div>
          <OuNotice tone="danger">{loadError}</OuNotice>
          <div style={{ marginTop: 12 }}>
            <OuSecondaryButton onClick={load}>Try again</OuSecondaryButton>
          </div>
        </div>
      )}

      {showTable ? (
        <OuTable<ExportHistoryRow>
          columns={entityType === "company" ? companyColumns : peopleColumns}
          rows={isLoading ? [] : rows}
          getRowId={(r) => r.id}
          loading={isLoading}
          selection={{ selected, onChange: setSelected }}
        />
      ) : (
        !loadError && (
          <OuEmptyState
            icon={<HistoryIcon />}
            title={hasFilters ? "No exports match" : entityType === "company" ? "No companies exported yet" : "No people exported yet"}
            hint={hasFilters ? "Change the search or remove a filter." : entityType === "company" ? "Exported companies will appear here." : "Exported people will appear here."}
          />
        )
      )}

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { ...popoverPaperSx, minWidth: "280px", maxWidth: "400px" } }}
      >
        <Paper sx={{ padding: "8px", boxShadow: "none", backgroundColor: "#f9f9fb" }}>
          <List sx={{ padding: 0, maxHeight: "300px", overflowY: "auto" }}>
            {availableFilters.map((type) => (
              <ListItem key={type} disablePadding sx={{ width: "100%" }}>
                <ListItemButton
                  onClick={() => {
                    openFilter(type, filterAnchorEl);
                    setFilterAnchorEl(null);
                  }}
                  sx={{ padding: "8px 16px", width: "100%", margin: 0, borderRadius: "6px", "&:hover": { backgroundColor: "var(--color-backgroundBrandTabHover, #f0eff2)" } }}
                >
                  <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundPrimary }}>
                    {FILTER_LABELS[type]}
                  </Text>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>

      {/* Filter Configuration Popover */}
      <Popover
        open={editingFilter !== null && configAnchorEl !== null}
        anchorEl={configAnchorEl}
        onClose={closeConfig}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { ...popoverPaperSx, minWidth: "400px", maxWidth: "400px" } }}
      >
        <Paper sx={{ padding: "16px", boxShadow: "none", backgroundColor: "#f9f9fb" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            {editingFilter === "date" ? (
              <Box
                onClick={() => setDraftMode(draftMode === "on" ? "between" : "on")}
                sx={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", userSelect: "none" }}
                title={draftMode === "on" ? "Switch to a range of days" : "Switch to a single day"}
              >
                <Text variant={TextVariant.md} style={{ fontWeight: 700, color: COLORS.foregroundPrimary, fontSize: "20px" }}>
                  {draftMode === "on" ? "on" : "between"}
                </Text>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M8 10.3 Q7.5 10 8 9.7 L11 9.7 Q12 9.5 13 9.7 L16 9.7 Q16.5 10 16 10.3 L12.2 14.5 Q12 14.7 11.8 14.5 L8 10.3 Z" fill={COLORS.foregroundPrimary} />
                </svg>
              </Box>
            ) : (
              <Box onClick={() => setDraftOperator(draftOperator === "is" ? "is not" : "is")} sx={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", userSelect: "none" }}>
                <Text variant={TextVariant.md} style={{ fontWeight: 700, color: COLORS.foregroundPrimary, fontSize: "20px" }}>
                  {draftOperator}
                </Text>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M8 10.3 Q7.5 10 8 9.7 L11 9.7 Q12 9.5 13 9.7 L16 9.7 Q16.5 10 16 10.3 L12.2 14.5 Q12 14.7 11.8 14.5 L8 10.3 Z" fill={COLORS.foregroundPrimary} />
                </svg>
              </Box>
            )}
            <Button
              onClick={() => {
                setFilters({});
                closeConfig();
              }}
              style={{ color: "var(--color-foregroundPrimary, #454262)", border: "none", backgroundColor: "transparent", padding: "4px 8px", minWidth: "auto" }}
              type="button"
            >
              Clear all
            </Button>
          </Box>

          <Box sx={{ marginBottom: "24px", width: "100%" }}>
            {editingFilter === "date" ? (
              draftMode === "on" ? (
                <Input type="date" label="Day" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              ) : (
                <Box sx={{ display: "flex", gap: "12px" }}>
                  <Input type="date" label="From" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                  <Input type="date" label="To" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                </Box>
              )
            ) : (
              editingFilter && (
                <Select
                  displayEmpty
                  fullWidth
                  multiple
                  options={options[editingFilter].map((o) => ({ value: o.value, label: o.label }))}
                  value={draftValues}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDraftValues(typeof value === "string" ? value.split(",") : (value as string[]));
                  }}
                  renderValue={(value) => {
                    const values = value as string[];
                    if (!values || values.length === 0) return <span style={{ color: COLORS.foregroundSecondary }}>Select values...</span>;
                    return values.map((v) => options[editingFilter].find((o) => o.value === v)?.label || v).join(", ");
                  }}
                  sx={selectSx}
                />
              )
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={applyFilter} style={{ backgroundColor: "var(--color-backgroundBrand, #e8eaf1)", color: COLORS.foregroundBrand, border: "none" }} type="button">
              Done
            </Button>
          </Box>
        </Paper>
      </Popover>
    </OuPage>
  );
};

export default ExportHistorySection;
